import { OFFER_TYPE_HOTEL, PRODUCT_TYPE_ULTRALUX } from 'constants/offer'
import LabelCountContext from 'contexts/LabelCountContext'
import type { OptimizelyExperimentVariantType } from 'hooks/Optimizely/useOptimizelyExperiment'
import { take } from 'lib/array/arrayUtils'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import PromotionLabel from 'LuxKit/Label/PromotionLabel'
import UrgencyLabel from 'LuxKit/Label/UrgencyLabel'
import LuxPlusLabel from 'LuxKit/Label/LuxPlusLabel'
import LuxPlusLabelWithModal from 'luxPlus/components/LuxPlusLabelWithModal'
import type { ComponentProps } from 'react'
import { forwardRef, Fragment, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import LuxPlusMonthLabel from 'takeovers/luxPlusMonthTakeover/components/LuxPlusMonthLabel'
import { LUX_PLUS_MONTH_TAKEOVER_ID } from 'takeovers/luxPlusMonthTakeover/constants/luxPlusMonthTakeoverConstants'
import { SiteTakeoverCampaignTagLabel } from 'takeovers/utils/components/SiteTakeoverCampaignTagLabel'
import { useCurrentSiteTakeover } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import OfferUrgencyCountdownLabel from './OfferUrgencyCountdownLabel'
import OfferUrgencyRoomCount from './OfferUrgencyRoomCount'
import AgentHubAdditionalCommissionLabel from 'agentHub/components/AgentHubAdditionalCommissionLabel'
import StructureLabel from 'LuxKit/Label/StructureLabel'
import LineKidIcon from 'LuxKit/Icons/line/LineKidIcon'
import OnlyInAustraliaCampaignLabel from './OnlyInAustraliaCampaignLabel'
import { isOnlyInAustraliaCampaignTag, offerHasOnlyInAustraliaCampaignTag } from 'lib/offer/onlyInAustraliaCampaignTag'

interface Props extends ComponentProps<typeof LabelGroup> {
  offer?: App.Offer | App.OfferSummary
  urgencyLabels?: Array<App.OfferUrgencyLabel>
  countDownShowHourOnly?: boolean
  saveAmount?: number
  showMemberInclusionLabels?: boolean
  luxPlusInclusions?: Array<App.PackageInclusion>
  pricingBlockOptimizelyExperimentVariant?: OptimizelyExperimentVariantType
  leadWithLuxPlusPrice?: boolean
  availableRoomCount?: number
  suppressTakeoverCampaignTagWhenUSPVisible?: boolean
  freeStayLabel?: string
  // TEMPORARY: Used by Lunar New Year takeover to show info icon with details modal on offer pages
  onOfferPage?: boolean
}

const OfferLabels = forwardRef<HTMLDivElement, Props>((props, ref) => {
  const {
    offer,
    urgencyLabels,
    countDownShowHourOnly = false,
    saveAmount,
    showMemberInclusionLabels,
    luxPlusInclusions,
    leadWithLuxPlusPrice,
    availableRoomCount,
    suppressTakeoverCampaignTagWhenUSPVisible = false,
    onOfferPage = false,
    freeStayLabel,
    ...rest
  } = props

  const maxLabels = useContext(LabelCountContext)
  const intl = useIntl()
  // Product requirement: do not show the Only in Australia label when the carousel is also enabled.
  const isOnlyInAustraliaCampaignFeatureEnabled = false
  const showOnlyInAustraliaCampaignLabel = isOnlyInAustraliaCampaignFeatureEnabled && offerHasOnlyInAustraliaCampaignTag(offer?.campaignTags)

  const labels = useMemo(() => {
    const offerLabels = urgencyLabels ?? offer?.urgencyTags ?? []

    // we unshift to add to the start...create a copy so we're not mutating the original
    const baseLabels = [...offerLabels]

    if (offer?.luxPlus?.access === 'earlyAccess') {
      baseLabels.unshift({ type: 'lux_plus_early_access' })
    } else if (offer?.luxPlus?.access === 'memberOnly') {
      baseLabels.unshift({ type: 'lux_plus_member_only' })
    } else if (saveAmount) {
      baseLabels.unshift({ type: 'lux_plus_member_price' })
    }

    if (offer?.luxPlus?.hasMemberInclusions && showMemberInclusionLabels) {
      baseLabels.unshift({ type: 'lux_plus_member_inclusions' })
    }

    if (offer?.isAgentHubExclusive) {
      baseLabels.unshift({ type: 'agenthub_exclusive' })
    }

    if (offer?.type === OFFER_TYPE_HOTEL && availableRoomCount && !baseLabels.find(urgencyLabel => urgencyLabel.type === 'left')) {
      baseLabels.unshift({ type: 'popular_option_room_count' })
    }

    if (offer?.campaignTags?.length) {
      baseLabels.unshift({ type: 'takeover_campaign_tag' })
    }

    if (showOnlyInAustraliaCampaignLabel) {
      baseLabels.unshift({ type: 'only_in_australia_campaign' })
    }

    if (freeStayLabel) {
      const leftIndex = baseLabels.findIndex(l => l.type === 'left')
      if (leftIndex >= 0) {
        baseLabels.splice(leftIndex + 1, 0, { type: 'children_travel_free', message: freeStayLabel })
      } else {
        baseLabels.push({ type: 'children_travel_free', message: freeStayLabel })
      }
    }

    return maxLabels ? take(baseLabels, maxLabels) : baseLabels
  }, [urgencyLabels, offer, saveAmount, showMemberInclusionLabels, maxLabels, availableRoomCount, freeStayLabel, showOnlyInAustraliaCampaignLabel])

  const isLuxPlusMonthTakeoverEnabled = useCurrentSiteTakeover({ offer })?.id === LUX_PLUS_MONTH_TAKEOVER_ID

  return (
    <LabelGroup {...rest} ref={ref}>
      <AgentHubAdditionalCommissionLabel offerId={offer?.id} />
      {labels.length > 0 && labels.map((label, index) => <Fragment key={`${label.type}-${index}`}>
        {/*
            The general release tag is only used for LuxPlus+ early access offers
            however the logic is slightly different to lux_plus_early_access so we need to handle it separately
        */}
        {label.type === 'lux_plus_general_release' && label.end && <OfferUrgencyCountdownLabel
          tagPlace="lux-plus-offer-label"
          endDate={label.end}
          showHourOnly={countDownShowHourOnly}
          isLuxPlusEarlyAccess
        />}

        {label.type === 'lux_plus_early_access' && offer && isLuxPlusMonthTakeoverEnabled && <LuxPlusMonthLabel type="early-access-offer" />}
        {label.type === 'lux_plus_early_access' && offer && !isLuxPlusMonthTakeoverEnabled && <LuxPlusLabelWithModal
          type="early-access-offer"
          saveAmount={saveAmount}
          offer={offer}
        />}

        {label.type === 'lux_plus_member_only' && !isLuxPlusMonthTakeoverEnabled && <LuxPlusLabel type="member-only-offer" />}
        {label.type === 'lux_plus_member_only' && isLuxPlusMonthTakeoverEnabled && <LuxPlusMonthLabel type="member-only-offer" />}

        {label.type === 'lux_plus_member_price' && offer && saveAmount && isLuxPlusMonthTakeoverEnabled && <LuxPlusMonthLabel type="member-price-offer" saveAmount={saveAmount} />}
        {label.type === 'lux_plus_member_price' && offer && saveAmount && !leadWithLuxPlusPrice && !isLuxPlusMonthTakeoverEnabled && <LuxPlusLabelWithModal
          type="member-price-offer"
          saveAmount={saveAmount}
          offer={offer as App.Offer}
          tileInclusions={offer.luxPlusTileInclusions}
        />}
        {label.type === 'lux_plus_member_inclusions' && offer && isLuxPlusMonthTakeoverEnabled && <LuxPlusMonthLabel
          type="member-inclusions"
        />}
        {label.type === 'lux_plus_member_inclusions' && offer && !isLuxPlusMonthTakeoverEnabled && <LuxPlusLabelWithModal
          type="bonus-inclusions"
          offer={offer}
          inclusions={luxPlusInclusions}
        />}
        {label.type === 'agenthub_exclusive' && <PromotionLabel variant="default">
          {intl.formatMessage({
            id: 'KDgYD7tNRcs2W8ObtnY-',
            defaultMessage: 'Agent Exclusive',
            description: 'Label for Agent Hub exclusive offers in the travel agent hub portal',
          })}
        </PromotionLabel>}
        {label.type === 'left' && label.end && <OfferUrgencyCountdownLabel
          endDate={label.end}
          showHourOnly={countDownShowHourOnly}
          tagPlace="offer-labels"
          // TODO: Remove this once '006RF00000JQyk1YAD' offer availability ends
          overrideShowWithinXDays={offer?.id === '006RF00000JQyk1YAD' ? 20 : undefined}
        />}
        {label.type === 'children_travel_free' && label.message && <StructureLabel variant="kids-free" startIcon={<LineKidIcon />}>{label.message}</StructureLabel>}
        {label.type === 'popular_option_room_count' && availableRoomCount && <OfferUrgencyRoomCount availableRoomCount={availableRoomCount} />}
        {label.type === 'ultralux' && <ProductTypeLabel productType={PRODUCT_TYPE_ULTRALUX}></ProductTypeLabel>}
        {label.type === 'new' && <UrgencyLabel variant="positive">
          {intl.formatMessage({
            id: 'CdJADYkoByiI9v8b7U5Y',
            defaultMessage: 'NEW',
            description: 'Badge label indicating that an offer is newly added to the marketplace',
          })}
        </UrgencyLabel>}
        {label.type === 'only_in_australia_campaign' && <OnlyInAustraliaCampaignLabel linkToCampaign={onOfferPage} />}
        {label.type === 'takeover_campaign_tag' && [...new Set(offer?.campaignTags ?? [])].filter(tag => !showOnlyInAustraliaCampaignLabel || !isOnlyInAustraliaCampaignTag(tag)).map((campaignTag, tagIndex) => <SiteTakeoverCampaignTagLabel
          key={`${campaignTag}-${tagIndex}`}
          campaignTagId={campaignTag}
          suppressWhenUSPVisible={suppressTakeoverCampaignTagWhenUSPVisible}
          onOfferCard
          onOfferPage={onOfferPage}
        />)}
      </Fragment>)}
    </LabelGroup>
  )
})

OfferLabels.displayName = 'OfferLabels'

export default OfferLabels
